import React, { useState } from "react";
import { Input, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * WhatsApp-style mockup of the event_thank_you_v1 message, sent manually (from Event Registrations'
 * "Send thank-you messages" action, once the event has ended) to guests who checked in.
 * Mirrors the approved Meta template line by line:
 *   "شكراً لحضوركم" -> guest name -> event title -> thank-you sentence -> closing line.
 *
 * Same editing convention as WhatsAppInvitationPreview: with `editable`, the thank-you sentence can
 * be changed right here, writing back to the parent through onChangeThankYouText so the input above
 * the preview (if any) and this bubble always agree. The guest name and event title are shown as
 * read-only samples (they're filled in per guest/event when the message is actually sent).
 */
const COPY = {
  heading: "🌹 شكراً لحضوركم 🌹",
  guestNameLabel: "الاسم:",
  guestNameSample: "اسم الضيف",
  titlePlaceholder: "سيظهر عنوان الفعالية هنا",
  closingLine: "نتمنى أن تكونوا قد استمتعتم بهذه المناسبة الجميلة.",
};

const paragraph = { margin: "0 0 10px" };
const editableStyle = {
  borderBottom: "1px dashed #128c7e",
  background: "rgba(18,140,126,0.07)",
  borderRadius: 3,
  padding: "0 3px",
  cursor: "text",
};

// Same inline-edit-on-click text line as WhatsAppInvitationPreview's InlineText.
const InlineText = ({ value, placeholder, maxLength, editable, onChange, fallback, hint }) => {
  const [editing, setEditing] = useState(false);

  if (!editable) return <span>{value || placeholder}</span>;

  if (editing) {
    return (
      <Input
        autoFocus
        size="small"
        dir="rtl"
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value.replace(/\s+/g, " "))}
        onBlur={() => {
          if (!String(value || "").trim() && fallback) onChange(fallback);
          setEditing(false);
        }}
        onPressEnter={() => setEditing(false)}
        style={{ direction: "rtl", fontSize: 13 }}
      />
    );
  }

  return (
    <Tooltip title={hint}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") setEditing(true);
        }}
        style={editableStyle}
      >
        {value || placeholder} <EditOutlined style={{ fontSize: 10, color: "#128c7e" }} />
      </span>
    </Tooltip>
  );
};

const ThankYouMessagePreview = ({
  editable = false,
  displayTitle,
  thankYouText,
  defaultThankYouText,
  onChangeThankYouText,
}) => {
  const { t } = useTranslation();
  const editHint = t("eventForm.whatsapp.previewClickToEdit");

  return (
    <div>
      <div
        style={{
          maxWidth: 340,
          width: "100%",
          margin: "0 auto",
          borderRadius: 24,
          border: "8px solid #1f1f1f",
          backgroundColor: "#e5ddd5",
          overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            backgroundColor: "#075e54",
            color: "#fff",
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            direction: "ltr",
          }}
        >
          <span style={{ fontSize: 18 }}>&larr;</span>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#128c7e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ✉
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Future Cards</div>
          <span style={{ fontSize: 16 }}>&#8942;</span>
        </div>

        <div style={{ padding: 14, minHeight: 160 }}>
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            <div style={{ padding: "10px 12px 6px", fontSize: 13, lineHeight: 1.8, color: "#111" }}>
              <p style={paragraph}>{COPY.heading}</p>

              {/* {{1}} guest name - filled in per guest when the message is sent */}
              <p style={paragraph}>
                {COPY.guestNameLabel} {COPY.guestNameSample}
              </p>

              {/* {{2}} event title */}
              <p style={paragraph}>
                🎊 {displayTitle || COPY.titlePlaceholder}
              </p>

              {/* {{3}} organiser's thank-you sentence */}
              <p style={paragraph}>
                <InlineText
                  value={thankYouText}
                  fallback={defaultThankYouText}
                  maxLength={200}
                  editable={editable}
                  onChange={onChangeThankYouText}
                  hint={editHint}
                />
              </p>

              <p style={{ ...paragraph, marginBottom: 2 }}>{COPY.closingLine}</p>

              <div style={{ textAlign: "left", direction: "ltr", fontSize: 11, color: "#667781", paddingBottom: 6 }}>
                21:15
              </div>
            </div>
          </div>
        </div>
      </div>

      {editable && (
        <div className="text-xs text-gray-500 mt-3">{t("eventForm.whatsapp.previewEditHint")}</div>
      )}
    </div>
  );
};

export default ThankYouMessagePreview;
